import { Card, CardActions, CardContent, Grid, Typography, useEventCallback } from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { memo, ReactElement, useCallback, useMemo } from 'react';
import * as yup from 'yup';
import Operator from './operator';
import Anpr from './anpr';
import Authorization from './authorization';
import {
  AnprForm,
  FormOperator as FormOperatorType,
  OperatorForm,
} from 'system/view-models/operator';
import { FormikErrors, FormikTouched } from 'formik';
import {
  regexEmail,
  // fiscalCodeRegex,
  alphanumericRegex,
  nameSurnameRegex,
  nameSurnameMoreCharactersRegex,
  regexOnlyNumber,
} from 'shell/constants/regex';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { FEATURE_AUTHORIZATION_ANPR } from './constants';
import ServicesTable from './services';

const validationSchema = yup
  .object({
    operator: yup.object().shape({
      code: yup
        .string()
        .nullable()
        // nuovamente obbligatorio
        .required('Campo obbligatorio')
        .matches(alphanumericRegex, 'Il campo può contenere solo lettere o numeri'),
      institution: yup.string().required('Campo obbligatorio'),
      name: yup
        .string()
        .required('Campo obbligatorio')
        .matches(nameSurnameMoreCharactersRegex, 'Formato non valido'),
      qualification: yup.string().required('Campo obbligatorio'),
      service: yup.string().required('Campo obbligatorio'),
      profile: yup.string().required('Campo obbligatorio'),
      typeOperator: yup.string(),
      email: yup.string().matches(regexEmail, 'Formato email'),
      organizationEmail: yup.string().matches(regexEmail, 'Formato email'),
      // fiscalCode: yup.string().matches(fiscalCodeRegex, 'Formato codice fiscale'),
      extCode: yup
        .string()
        .required('Campo obbligatorio')
        .matches(
          nameSurnameRegex,
          'Non sono ammessi caratteri speciali tranne - (trattino) e . (punto)'
        ),
      codAna: yup.string().nullable().matches(regexOnlyNumber, 'Il campo è un numero'),
    }),
    anpr: yup.object().shape({
      // stationId: yup
      //   .string()
      //   .matches(regexOnlyNumber, 'Sono ammesse solamente cifre')
      //   .nullable()
      //   .required('Campo obbligatorio'),
      isRequiredAnpr: yup.boolean().nullable().notRequired(),
      isEditAnprData: yup.boolean().nullable().notRequired(),
      stationIdAnpr: yup.object().nullable(),
      anprOperatorCode: yup.string().nullable(),
      validityEnd: yup.date().nullable().notRequired().typeError('Data non valida'),
      validityStart: yup
        .date()
        .nullable()
        .when('validityEnd', (validityEnd, formValidationSchema) =>
          validityEnd
            ? formValidationSchema.max(
                validityEnd,
                'La data deve essere minore della data di fine validità'
              )
            : formValidationSchema.nullable()
        ),
    }),
  })
  .test('operator.typeOperator', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as OperatorForm;

    if (
      !castedValues.operator?.typeOperator &&
      castedValues.feature?.authorizationsList?.some(
        (item) =>
          Number.parseInt(item.id || '') === FEATURE_AUTHORIZATION_ANPR &&
          (item.value?.split(' ')[0] === '1' || item.value?.split(' ')[0] === '2')
      )
    ) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'operator.typeOperator');
    }

    return true;
  })
  .test('operator.fiscalCode', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as OperatorForm;

    if (
      !castedValues.operator?.fiscalCode &&
      (castedValues.anpr?.isEditAnprData || castedValues.anpr?.isRequiredAnpr)
    ) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'operator.fiscalCode');
    }

    return true;
  })
  .test('anpr.anprOperatorCode', 'Campo obbligatorio, compilare Codice fiscale', (values) => {
    const castedValues = values as unknown as OperatorForm;

    if (
      !castedValues.anpr?.anprOperatorCode &&
      (castedValues.anpr?.isEditAnprData || castedValues.anpr?.isRequiredAnpr)
    ) {
      return new yup.ValidationError(
        'Campo obbligatorio, compilare Codice fiscale',
        undefined,
        'anpr.anprOperatorCode'
      );
    }

    return true;
  })
  .test('anpr.stationIdAnpr', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as OperatorForm;

    if (
      !castedValues.anpr?.stationIdAnpr?.id &&
      (castedValues.anpr?.isEditAnprData || castedValues.anpr?.isRequiredAnpr)
    ) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'anpr.stationIdAnpr');
    }

    return true;
  })
  .test('anpr.validityStart', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as OperatorForm;

    if (
      !castedValues.anpr?.validityStart &&
      (castedValues.anpr?.isEditAnprData || castedValues.anpr?.isRequiredAnpr)
    ) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'anpr.validityStart');
    }

    return true;
  });

interface FormOperatorProps {
  onOk: (form?: OperatorForm) => void;
  onCancel: (form?: OperatorForm) => void;
  title: string;
  children?: ReactElement;
  isDetail?: boolean;
  disabled?: boolean;
  isEdit?: boolean;
  initialValues?: OperatorForm;
}

const FormOperatorComponent = ({
  onOk,
  onCancel,
  title,
  isDetail = false,
  isEdit,
  disabled = false,
  initialValues,
}: FormOperatorProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const operatorForm = useFormik<OperatorForm>({
    initialValues: initialValues || {
      feature: {},
      services: { page: 0, rowsPerPage: 10, servicesList: [] },
      operator: {
        name: '',
        institution: '',
        qualification: '',
        service: '',
        profile: '',
        typeOperator: '',
        email: '',
        organizationEmail: '',
        fiscalCode: '',
        extCode: '',
        code: '',
      },
      anpr: {
        anprOperatorCode: '',
        stationIdAnpr: null,
        validityStart: null,
      },
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const handleChangeAuthorization = useCallback(
    (key, value) => {
      operatorForm.setFieldValue('feature', {
        ...operatorForm.values.feature,
        [key]: value,
      });
    },
    [operatorForm.setFieldValue, operatorForm.values.feature]
  );

  const handleChangeOpeartor = useCallback(
    (key, value) => {
      operatorForm.setFieldValue('operator', {
        ...operatorForm.values.operator,
        [key]: value,
      });
    },
    [operatorForm.setFieldValue, operatorForm.values.operator]
  );

  const handleChangeTypeOperatore = useCallback(
    (item) => {
      operatorForm.setFieldValue('operator', {
        ...operatorForm.values.operator,
        typeOperator: item?.id,
      });

      if (
        (item?.id === '1' || item?.id === '2') &&
        operatorForm.values?.feature?.authorizationsList?.some(
          (element) =>
            Number.parseInt(element.id || '') === FEATURE_AUTHORIZATION_ANPR &&
            (element.value?.split(' ')[0] === '1' || element.value?.split(' ')[0] === '2')
        )
      ) {
        const idNumber = item?.stationIdAnpr?.id;

        const stationDescription = item?.stationIdAnpr?.description || '';

        operatorForm.setFieldValue('anpr', {
          ...operatorForm.values.anpr,
          isRequiredAnpr: true,
          anprOperatorCode: cfSindaco,
          stationIdAnpr: {
            id: idNumber ? Number.parseInt(idNumber, 10) : undefined,
            description: stationDescription || '',
          },
        });
      } else if (
        item?.id === '0' &&
        operatorForm.values?.feature?.authorizationsList?.some(
          (element) =>
            Number.parseInt(element.id || '') === FEATURE_AUTHORIZATION_ANPR &&
            (element.value?.split(' ')[0] === '1' || element.value?.split(' ')[0] === '2')
        )
      ) {
        operatorForm.setFieldValue('anpr', {
          ...operatorForm.values.anpr,
          isEditAnprData: true,
          isRequiredAnpr: true,
          anprOperatorCode: operatorForm.values?.operator?.fiscalCode || '',
          validityStart: null,
          validityEnd: null,
          stationIdAnpr: undefined,
        });
      } else {
        operatorForm.setFieldValue('anpr', {
          ...operatorForm.values.anpr,
          isRequiredAnpr: false,
          isEditAnprData: false,
          anprOperatorCode: '',
          validityStart: null,
          validityEnd: null,
          stationIdAnpr: null,
        });
      }
    },
    [
      operatorForm.setFieldValue,
      operatorForm.values.operator,
      operatorForm.values.feature,
      operatorForm.values.anpr,
    ]
  );

  const handleChangeAnpr = useEventCallback(
    (key: keyof AnprForm, value: AnprForm[keyof AnprForm]) => {
      operatorForm.setFieldValue('anpr', {
        ...operatorForm.values.anpr,
        [key]: value,
      });
    }
  );

  const handleChanges = useEventCallback((values: OperatorForm) => {
    const merged = {
      ...(operatorForm.values || {}),
      operator: {
        ...(operatorForm.values.operator || {}),
        ...values.operator,
      },
      anpr: {
        ...(operatorForm.values?.anpr || {}),
        ...(values.anpr || {}),
      },
      feature: {
        ...(operatorForm.values?.feature || {}),
        ...(values.feature || {}),
      },
    };
    operatorForm.setValues(merged);
  });

  const handleChangesAnpr = useEventCallback((values: AnprForm) => {
    operatorForm.setFieldValue('anpr', {
      ...(operatorForm.values.anpr || {}),
      ...values,
    });
  });

  const handleChangeServices = useCallback(
    (key, value) => {
      operatorForm.setFieldValue('services', {
        ...operatorForm.values.services,
        [key]: value,
      });
    },
    [operatorForm.setFieldValue, operatorForm.values.services]
  );

  const { userProfile } = useUserProfile();

  const cfSindaco = userProfile.datiLogin?.profile?.codiceFiscaleSindaco
    ? userProfile.datiLogin.profile.codiceFiscaleSindaco
    : undefined;

  const anprAuthorization = useMemo(() => {
    if (operatorForm.values?.operator?.typeOperator === '0') {
      if (
        operatorForm.values?.feature?.authorizationsList?.some(
          (item) =>
            Number.parseInt(item.id || '') === FEATURE_AUTHORIZATION_ANPR &&
            (item.value?.split(' ')[0] === '1' || item.value?.split(' ')[0] === '2')
        )
      ) {
        return false;
      }

      return true;
    } else if (
      operatorForm.values?.operator?.typeOperator === '1' ||
      operatorForm.values?.operator?.typeOperator === '2'
    ) {
      if (
        operatorForm.values?.feature?.authorizationsList?.some(
          (item) =>
            Number.parseInt(item.id || '') === FEATURE_AUTHORIZATION_ANPR &&
            (item.value?.split(' ')[0] === '1' || item.value?.split(' ')[0] === '2')
        )
      ) {
        return true;
      }

      return true;
    } else return true;
  }, [operatorForm.values]);

  const disabledTypeOperator = useMemo(() => {
    return !operatorForm.values?.feature?.authorizationsList?.some(
      (item) =>
        Number.parseInt(item.id || '') === FEATURE_AUTHORIZATION_ANPR &&
        (item.value?.split(' ')[0] === '1' || item.value?.split(' ')[0] === '2')
    );
  }, [operatorForm.values?.feature]);

  const operatorServices = useMemo(() => {
    return (
      operatorForm.values.services?.servicesList.map((item) => {
        return {
          id: item,
        };
      }) || []
    );
  }, [operatorForm.values.services?.servicesList]);

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={operatorForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Operator
                  isEdit={isEdit}
                  operatorForm={operatorForm.values.operator}
                  onChange={handleChangeOpeartor}
                  onChangeTypeOperator={handleChangeTypeOperatore}
                  onChangeAnpr={handleChangeAnpr}
                  onChangeValues={handleChanges}
                  disabled={disabled}
                  disabledTypeOperator={disabledTypeOperator || disabled}
                  errors={operatorForm.errors.operator as FormikErrors<FormOperatorType>}
                  touched={
                    operatorForm.touched.operator as unknown as FormikTouched<FormOperatorType>
                  }
                  isRequiredFiscalCode={
                    operatorForm.values.anpr?.isEditAnprData ||
                    operatorForm.values.anpr?.isRequiredAnpr
                  }
                  isRequiredTypeOperator={!disabledTypeOperator}
                />
              </Grid>
              <Grid item xs={12}>
                <Anpr
                  anprForm={operatorForm.values.anpr}
                  onChanges={handleChangesAnpr}
                  title="ANPR"
                  isDetail={isDetail}
                  disabled={disabled || anprAuthorization}
                  errors={operatorForm.errors.anpr as FormikErrors<AnprForm>}
                  touched={operatorForm.touched.anpr as unknown as FormikTouched<AnprForm>}
                  fiscalCode={
                    operatorForm.values?.operator?.typeOperator === '1' ||
                    operatorForm.values?.operator?.typeOperator === '2'
                      ? cfSindaco
                      : operatorForm.values.operator?.fiscalCode
                  }
                />
                <Authorization
                  title="Autorizzazioni"
                  disabled={disabled}
                  fiscalCode={
                    operatorForm.values?.operator?.typeOperator === '1' ||
                    operatorForm.values?.operator?.typeOperator === '2'
                      ? cfSindaco
                      : operatorForm.values.operator?.fiscalCode
                  }
                  typeOperator={operatorForm.values?.operator?.typeOperator || ''}
                  features={operatorForm.values.feature}
                  handleChange={handleChangeAuthorization}
                  handleChanges={handleChanges}
                />
                <ServicesTable
                  operatorServices={operatorServices}
                  disabled={disabled}
                  onChange={handleChangeServices}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(operatorForm.values);
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormOperatorComponent.displayName = 'Form Institution component';

const FormOperator = memo(FormOperatorComponent);

export default FormOperator;
